import { DocsService } from '@affine/core/modules/doc';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import type { BranchMeta } from '../handlers/create-branch';
import { BRANCHES_CHANGED_EVENT } from '../types';

export interface BranchEntry {
  /** Doc id of the branch (i.e. the child doc). */
  branchDocId: string;
  /** Doc id this branch was spawned from. */
  parentDocId: string;
  /** Block id of the source passage inside the parent doc (may be empty). */
  parentBlockId?: string;
  /** Verbatim snippet captured at create-time. */
  sourcePassage: string;
  /** ISO timestamp. */
  createdAt: string;
}

export interface BranchGraph {
  /** Set of doc ids that ARE branches (so the doc list can filter them out). */
  branchDocIds: ReadonlySet<string>;
  /** parentDocId → its branches, sorted newest-first. */
  branchesByParent: ReadonlyMap<string, ReadonlyArray<BranchEntry>>;
  /** Convenience: look up branches for a single parent. Returns [] when none. */
  getBranches: (parentDocId: string) => ReadonlyArray<BranchEntry>;
}

const EMPTY_GRAPH: BranchGraph = {
  branchDocIds: new Set(),
  branchesByParent: new Map(),
  getBranches: () => [],
};

/**
 * Reactive branch graph for the active workspace.
 *
 * Reads every non-trashed doc's properties (via
 * `DocsService.allDocProperties$`) and decodes `custom:branchMeta` —
 * the single source of truth we persist at create-time. Updates
 * automatically when properties change AND on the explicit
 * `BRANCHES_CHANGED_EVENT` so toast-undo (which mutates trash state
 * outside the propertiesStore stream) refreshes immediately too.
 *
 * Use this hook from anywhere that needs to know:
 *   - "is THIS doc a branch?" (the doc list filter)
 *   - "what branches does THIS doc have?" (the doc list collapse UI)
 *   - "how many branches in the workspace?" (the floating panel)
 */
export function useBranchGraph(): BranchGraph {
  const docsService = useService(DocsService);
  const allProps = useLiveData(docsService.allDocProperties$);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick(t => t + 1);
    window.addEventListener(BRANCHES_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(BRANCHES_CHANGED_EVENT, onChange);
  }, []);

  return useMemo<BranchGraph>(() => {
    void tick;
    if (!allProps) return EMPTY_GRAPH;
    const branchDocIds = new Set<string>();
    const byParent = new Map<string, BranchEntry[]>();
    for (const [docId, props] of Object.entries(allProps)) {
      const raw = (props as Record<string, unknown>)['custom:branchMeta'];
      if (typeof raw !== 'string') continue;
      let parsed: BranchMeta | undefined;
      try {
        parsed = JSON.parse(raw) as BranchMeta;
      } catch {
        continue;
      }
      if (!parsed?.parentDocId) continue;
      branchDocIds.add(docId);
      const entry: BranchEntry = {
        branchDocId: docId,
        parentDocId: parsed.parentDocId,
        parentBlockId: parsed.parentBlockId,
        sourcePassage: parsed.sourcePassage ?? '',
        createdAt: parsed.createdAt ?? '',
      };
      const list = byParent.get(parsed.parentDocId) ?? [];
      list.push(entry);
      byParent.set(parsed.parentDocId, list);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
    return {
      branchDocIds,
      branchesByParent: byParent,
      getBranches: (parentDocId: string) => byParent.get(parentDocId) ?? [],
    };
  }, [allProps, tick]);
}
